import { z } from "zod/v4";

import {
  CreateExperienceKeyMetricDtoDescriptions,
  ExperienceKeyMetricDtoDescriptions,
} from "../descriptions/experience-key-metric-dto.describe";
import { assertDescriptions } from "../descriptions/types";

import { StandardEventAllowedKeyMetricsList } from "./analytics-events/standard-events";

// Base object schema without refinements - used for extension
const ExperienceKeyMetricBaseSchema = z.object({
  id: z.uuid().describe(ExperienceKeyMetricDtoDescriptions.id),
  experienceId: z
    .uuid()
    .describe(ExperienceKeyMetricDtoDescriptions.experienceId),
  order: z
    .number()
    .int()
    .nonnegative("Order must be a non-negative integer")
    .describe(ExperienceKeyMetricDtoDescriptions.order),
  isPrimary: z
    .boolean()
    .default(false)
    .describe(ExperienceKeyMetricDtoDescriptions.isPrimary),
  standardEventId: z
    .enum(StandardEventAllowedKeyMetricsList)
    .nullish()
    .describe(ExperienceKeyMetricDtoDescriptions.standardEventId),
  experienceCustomMetricId: z
    .uuid()
    .nullish()
    .describe(ExperienceKeyMetricDtoDescriptions.experienceCustomMetricId),
});

// Refinement: either standardEventId or experienceCustomMetricId must be specified
const metricIdRefinement = <
  T extends {
    standardEventId?: string | null;
    experienceCustomMetricId?: string | null;
  },
>(
  metric: T
): boolean =>
  metric.standardEventId != null || metric.experienceCustomMetricId != null;

const metricIdRefinementMessage =
  "Either standardEventId or experienceCustomMetricId must be specified";

export const ExperienceKeyMetricDtoSchema =
  ExperienceKeyMetricBaseSchema.refine(metricIdRefinement, {
    message: metricIdRefinementMessage,
  });

export type ExperienceKeyMetricDto = z.infer<
  typeof ExperienceKeyMetricDtoSchema
>;

// Base schema for create operations - use for .omit()/.extend() operations
export const CreateExperienceKeyMetricBaseSchema =
  ExperienceKeyMetricBaseSchema.extend({
    id: z
      .uuid()
      .optional()
      .describe(CreateExperienceKeyMetricDtoDescriptions.id),
    experienceId: z
      .uuid()
      .optional()
      .describe(CreateExperienceKeyMetricDtoDescriptions.experienceId),
    order: ExperienceKeyMetricBaseSchema.shape.order.describe(
      CreateExperienceKeyMetricDtoDescriptions.order
    ),
    isPrimary: ExperienceKeyMetricBaseSchema.shape.isPrimary.describe(
      CreateExperienceKeyMetricDtoDescriptions.isPrimary
    ),
    standardEventId:
      ExperienceKeyMetricBaseSchema.shape.standardEventId.describe(
        CreateExperienceKeyMetricDtoDescriptions.standardEventId
      ),
    experienceCustomMetricId:
      ExperienceKeyMetricBaseSchema.shape.experienceCustomMetricId.describe(
        CreateExperienceKeyMetricDtoDescriptions.experienceCustomMetricId
      ),
  });

// Full schema with refinement - use for runtime validation
export const CreateExperienceKeyMetricDtoSchema =
  CreateExperienceKeyMetricBaseSchema.refine(metricIdRefinement, {
    message: metricIdRefinementMessage,
  });

export type CreateExperienceKeyMetricDto = z.infer<
  typeof CreateExperienceKeyMetricDtoSchema
>;

// Compile-time check: description object must cover all schema keys
assertDescriptions<
  typeof ExperienceKeyMetricDtoDescriptions,
  ExperienceKeyMetricDto
>();
